import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsSplitUpAndLeft,
  faChartLine,
  faDatabase,
  faFunnelDollar,
  faLightbulb,
  faRobot,
  faScrewdriverWrench,
} from "@fortawesome/free-solid-svg-icons";
import { cn, mapEdgesToNodes, toPlainText } from "../../lib/helpers";
import PortableText from "../portableText";

const CATEGORIES = [
  { title: "Core Concepts", value: "core-concepts", icon: faLightbulb },
  { title: "Data & Identity", value: "data-identity", icon: faDatabase },
  { title: "Buyer Activity & Intent", value: "buyer-activity-intent", icon: faChartLine },
  { title: "GTM Execution", value: "gtm-execution", icon: faArrowsSplitUpAndLeft },
  { title: "AI & Automation", value: "ai-automation", icon: faRobot },
  { title: "Revenue & Funnel", value: "revenue-funnel", icon: faFunnelDollar },
  { title: "Ecosystem & Tools", value: "ecosystem-tools", icon: faScrewdriverWrench },
];

const query = graphql`
  query GlossaryListQuery {
    docs: allSanityGlossary(
      sort: { fields: [title], order: [ASC] }
      filter: {
        status: { in: ["published"] }
        slug: { current: { ne: null } }
      }
    ) {
      edges {
        node {
          id
          title
          category
          slug {
            current
          }
          _rawWhyItMatters(resolveReferences: { maxDepth: 12 })
          _rawBody(resolveReferences: { maxDepth: 12 })
        }
      }
    }
  }
`;

const GLOSSARY_URL = "https://www.commonroom.io/glossary/";

const generateGlossarySchema = (docs) => {
  if (!docs.length) return null;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "DefinedTermSet",
        "@id": `${GLOSSARY_URL}#glossary`,
        "name": "Common Room Glossary",
        "url": GLOSSARY_URL,
        "hasDefinedTerm": docs.map((doc) => ({
          "@type": "DefinedTerm",
          "@id": `${GLOSSARY_URL}#${doc.slug.current}`,
          "name": doc.title,
          "description": toPlainText(doc._rawBody),
          "url": `${GLOSSARY_URL}#${doc.slug.current}`,
          "inDefinedTermSet": `${GLOSSARY_URL}#glossary`,
        })),
      },
      {
        "@type": "FAQPage",
        "mainEntity": docs.map((doc) => ({
          "@type": "Question",
          "name": `What is ${doc.title}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": [toPlainText(doc._rawBody), toPlainText(doc._rawWhyItMatters)]
              .filter(Boolean)
              .join("\n\n"),
          },
        })),
      },
    ],
  };
};

export const GlossaryList = () => {
  const data = useStaticQuery(query) || {};
  const docs = data?.docs ? mapEdgesToNodes(data.docs) : [];
  const glossarySchema = generateGlossarySchema(docs);

  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].value);

  return (
    <div className="flex flex-col md:flex-row">
      {glossarySchema && (
        <Helmet
          script={[{
            type: "application/ld+json",
            innerHTML: JSON.stringify(glossarySchema),
          }]}
        />
      )}
      {/* Category sidebar */}
      <div className="flex-shrink-0 mt-6 flex flex-col gap-2 self-start sticky top-24">
        {CATEGORIES.map((cat) => {
          const isActive = cat.value === activeCategory;
          return (
            <a
              key={cat.value}
              href={`#${cat.value}`}
              onClick={() => setActiveCategory(cat.value)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-4 pr-6 text-left text-lg group transition-colors border-l-4 cursor-pointer font-semibold rounded-l-md",
                `${isActive ?
                  "bg-light-80 border-green-emerald text-black" :
                  "border-transparent hover:border-link hover:text-link"}`
              )}
            >
              <FontAwesomeIcon icon={cat.icon} className={cn(
                "h-4 flex-shrink-0",
                isActive ? "text-link" : "text-dark-40 group-hover:text-link"
              )} />
              <h2 className={isActive ? "text-link" : "group-hover:text-link"}>
                {cat.title}
              </h2>
            </a>
          );
        })}
      </div>

      {/* Items panel */}
      <div className="flex-1 flex flex-col gap-6">
        {CATEGORIES.map((cat) => {
          const items = docs.filter((d) => d.category === cat.value);
          if (!items.length) return null;
          return (
            <div key={cat.value} id={cat.value} style={{ scrollMarginTop: "6rem" }} className="p-6 md:p-8 bg-light-80 rounded-xl">
              <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <FontAwesomeIcon icon={cat.icon} className="h-5 text-green-emerald" />
                {cat.title}
              </h2>
              <ul className="flex flex-col gap-12">
                {items.map((item, i) => (
                  <li
                    key={item.id}
                    id={item.slug.current}
                    style={{ scrollMarginTop: "6rem" }}
                    className={cn(
                      "break-inside-avoid",
                      i > 0 ? "pt-12 border-t border-dark-20" : null
                    )}
                  >
                    <h3 className="font-medium text-2xl mb-4">
                      <a href={`#${item.slug.current}`} className="group hover:text-link">
                        {item.title}
                        <span className="ml-2 text-dark-40 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">#</span>
                      </a>
                    </h3>
                    <PortableText blocks={item._rawBody} className="leading-relaxed mb-4" />
                    <h4 className="font-semibold mb-1 text-secondary">Why It Matters</h4>
                    <PortableText blocks={item._rawWhyItMatters} className="leading-relaxed" />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};
